import { useRef, useState } from "react";
import { useJourneyWorkspace } from "../../js/hooks/useJourneyWorkspace";
import { FrameZoomProvider, useFrameZoom } from "../../js/hooks/useFrameZoom";
import FrameZoomControls from "./FrameZoomControls";
import BuyerNotesPanel from "./BuyerNotesPanel";
import BuyerTipsPanel from "./BuyerTipsPanel";
import BuyerTransactionPanel from "./BuyerTransactionPanel";
import SellerNotesPanel from "./SellerNotesPanel";
import SellerTipsPanel from "./SellerTipsPanel";
import SellerReadinessPanel from "./SellerReadinessPanel";
import ResourcesLink from "../layout/ResourcesLink";
import FrameReturnTop from "../layout/FrameReturnTop";
import { useContainedScroll } from "../../js/hooks/useContainedScroll";
import SuggestionWidget from "../suggest/SuggestionWidget";
import { useAuth } from "../../js/hooks/useAuth";
import { manualsUrl, JOURNEY_LINK_PROPS } from "../../js/utils/siteLinks";
import GuestLockShell from "../guest/GuestLockShell";

const BUYER_ITEMS = [
  { id: "notes", label: "Notes" },
  { id: "tips", label: "Tips" },
  { id: "transaction", label: "Transaction" },
];

const SELLER_ITEMS = [
  { id: "notes", label: "Notes" },
  { id: "tips", label: "Tips" },
  { id: "readiness", label: "Readiness" },
];

export default function JourneySidebar() {
  const { mode } = useJourneyWorkspace();

  return (
    <FrameZoomProvider storageKey="dh_sidebar_zoom_v3" defaultZoom={1}>
      <JourneySidebarFrame mode={mode} />
    </FrameZoomProvider>
  );
}

function JourneySidebarFrame({ mode }) {
  const { session } = useAuth();
  const { tab, setTab } = useJourneyWorkspace();
  const { frameZoom } = useFrameZoom();
  const bodyRef = useRef(null);
  const { showTop, returnToTop } = useContainedScroll(bodyRef);
  const items = mode === "seller" ? SELLER_ITEMS : BUYER_ITEMS;
  const [open, setOpen] = useState(false);

  return (
    <aside
      className={`journey-sidebar journey-sidebar--${mode}${open ? " is-open" : ""}`}
      aria-label={`${mode} companion`}
      data-tour={`${mode}-sidebar`}
    >
      <button
        type="button"
        className="journey-sidebar__toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? "Hide notes & tips" : "Notes, tips & guidance"}
      </button>
      <div className="journey-sidebar__chrome" data-tour="sidebar-chrome">
        <nav className="journey-mini-nav" aria-label="Sidebar sections">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`journey-mini-nav__btn${tab === item.id ? " is-active" : ""}`}
              data-tour={`sidebar-${item.id}`}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <FrameZoomControls label="Sidebar zoom" />
      </div>
      <div className="journey-sidebar__body" ref={bodyRef}>
        <div className="journey-sidebar__zoom-surface" style={{ zoom: frameZoom }}>
          <GuestLockShell className="guest-lock--sidebar">
            {mode === "buyer" && tab === "notes" ? <BuyerNotesPanel /> : null}
            {mode === "buyer" && tab === "tips" ? <BuyerTipsPanel /> : null}
            {mode === "buyer" && tab === "transaction" ? <BuyerTransactionPanel /> : null}
            {mode === "seller" && tab === "notes" ? <SellerNotesPanel /> : null}
            {mode === "seller" && tab === "tips" ? <SellerTipsPanel /> : null}
            {mode === "seller" && tab === "readiness" ? <SellerReadinessPanel /> : null}
            <SuggestionWidget compact />
          </GuestLockShell>
          <ResourcesLink className="sidebar-link resources-link" tour="sidebar-resources" />
          {mode === "buyer" ? (
            <a className="sidebar-link" href={manualsUrl("buyer", session)} {...JOURNEY_LINK_PROPS}>
              User Guide
            </a>
          ) : null}
          {mode === "seller" ? (
            <a className="sidebar-link" href={manualsUrl("seller", session)} {...JOURNEY_LINK_PROPS}>
              User Guide
            </a>
          ) : null}
        </div>
        <FrameReturnTop visible={showTop} onClick={returnToTop} />
      </div>
    </aside>
  );
}
